<template>
  <div class="mcp-playground">
    <!-- Connection status header -->
    <Card class="mb-4">
      <div class="p-4 flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <Badge :variant="isConnected ? 'success' : 'primary'">
              {{ isConnected ? 'Connected' : 'Disconnected' }}
            </Badge>
            <span class="text-sm text-gray-600 dark:text-gray-400">
              {{
                isConnected
                  ? `${tools.length} tools available`
                  : 'Not connected to MCP server'
              }}
            </span>
          </div>
          <div class="flex gap-2">
            <Button
              v-if="!isConnected"
              @click="handleConnect"
              :disabled="isLoading || !selectedServerSlug"
              size="sm"
            >
              <LoadingSpinner v-if="isLoading" class="mr-2 h-4 w-4" />
              {{ isLoading ? 'Connecting...' : 'Connect' }}
            </Button>
            <Button v-else @click="handleDisconnect" variant="outline" size="sm">
              Disconnect
            </Button>
          </div>
        </div>

        <!-- Server Selection -->
        <div class="flex items-center gap-4">
          <div class="w-full max-w-md">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Virtual Server
            </label>
            <Select
              v-model="selectedServerSlug"
              :options="serverOptions"
              :disabled="isConnected || isLoading"
              placeholder="Select virtual server"
            />
            <p v-if="activeServerCount === 0" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              No active virtual servers. Enable one in MCP Gateway → Virtual Servers.
            </p>
          </div>
        </div>
      </div>
    </Card>

    <!-- Error alert -->
    <Alert v-if="error" variant="error" class="mb-4">
      <div class="flex items-start justify-between">
        <div>
          <p class="text-sm font-semibold mb-1">Connection Error</p>
          <p class="text-sm">{{ error }}</p>
        </div>
        <Button @click="handleConnect" variant="outline" size="sm" class="ml-4">Retry</Button>
      </div>
    </Alert>

    <!-- Main content -->
    <div v-if="isConnected" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div class="lg:col-span-1">
        <Card>
          <div class="p-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Available Tools
            </h3>
            <ToolList
              :tools="tools"
              :selected-tool-id="selectedTool?.id"
              @select="handleSelectTool"
            />
          </div>
        </Card>
      </div>

      <div class="lg:col-span-2">
        <ToolInspector
          ref="toolInspectorRef"
          :tool="selectedTool"
          @execute="handleExecuteTool"
        />
      </div>
    </div>

    <!-- Not connected state -->
    <div v-else-if="!isLoading && !error" class="text-center py-12">
      <Card class="max-w-md mx-auto">
        <div class="p-8">
          <p class="text-gray-600 dark:text-gray-400">
            Select a virtual server and connect to inspect and execute its tools.
          </p>
        </div>
      </Card>
    </div>

    <!-- Loading state -->
    <div v-else-if="isLoading && !isConnected" class="text-center py-12">
      <LoadingSpinner class="h-8 w-8 mx-auto mb-4" />
      <p class="text-gray-600 dark:text-gray-400">Connecting to MCP server...</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, watch, computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import {
    Card,
    Button,
    Alert,
    Badge,
    LoadingSpinner,
    Select,
    useNotification,
  } from '@asabdev/asab-ui';
  import { useOrganizationsStore } from '@/stores/organizations';
  import { useMcpVServersStore } from '@/stores/mcpVServers';
  import { McpProtocolClient } from '@/lib/mcp/protocolClient';
  import { MCP_GATEWAY_BASE_URL } from '@/lib/api/config';
  import type { McpTool as LibMcpTool } from '@/lib/mcp/types';
  import type { McpTool, ToolResponse } from '../../types';
  import ToolList from './ToolList.vue';
  import ToolInspector from './ToolInspector.vue';
import { toUserMessage } from '@/lib/api/errors';

  interface Props {
    authMethod: 'bearer' | 'apiKey';
    authToken: string;
  }

  const props = defineProps<Props>();
  const { showSuccess, showError } = useNotification();

  const orgStore = useOrganizationsStore();
  const vserversStore = useMcpVServersStore();
  const { currentOrganizationId } = storeToRefs(orgStore);
  const { virtualServers } = storeToRefs(vserversStore);

  const toolInspectorRef = ref<InstanceType<typeof ToolInspector> | null>(null);

  const tools = ref<McpTool[]>([]);
  const selectedTool = ref<McpTool | null>(null);
  const isConnected = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const selectedServerSlug = ref<string>('');
  let client: McpProtocolClient | null = null;

  const activeServers = computed(() =>
    virtualServers.value.filter((s) => s.is_active),
  );
  const activeServerCount = computed(() => activeServers.value.length);

  const serverOptions = computed(() => {
    const opts = activeServers.value.map((vs) => ({
      label: vs.display_name || vs.slug,
      value: vs.slug,
    }));
    if (opts.length === 0) {
      return [{ label: 'No active virtual servers', value: '' }];
    }
    return opts;
  });

  const authHeaders = computed(() => {
    const token = props.authToken.trim();
    return props.authMethod === 'apiKey'
      ? { 'X-API-Key': token }
      : { Authorization: `Bearer ${token}` };
  });

  function buildClient(slug: string): McpProtocolClient {
    const base = (MCP_GATEWAY_BASE_URL ?? '').replace(/\/+$/, '');
    if (!base) {
      throw new Error('VITE_MCP_GATEWAY_BASE_URL is not configured');
    }
    const c = new McpProtocolClient(base, slug);
    c.setAuthHeaders(authHeaders.value as unknown as Record<string, string>);
    return c;
  }

  function adaptTools(raw: LibMcpTool[]): McpTool[] {
    return raw.map((t) => ({
      id: t.name,
      name: t.name,
      description: t.description ?? '',
      inputSchema:
        (t.inputSchema as unknown as McpTool['inputSchema']) ?? {
          type: 'object',
          properties: {},
        },
      outputSchema: t.outputSchema as McpTool['outputSchema'],
    }));
  }

  async function handleConnect() {
    if (!selectedServerSlug.value) return;
    isLoading.value = true;
    error.value = null;
    try {
      client = buildClient(selectedServerSlug.value);
      await client.initialize();
      const fetched = await client.listTools();
      tools.value = adaptTools(fetched);
      isConnected.value = true;
      showSuccess(`Connected — ${tools.value.length} tools available`);
    } catch (e) {
      const msg = toUserMessage(e, 'Failed to connect');
      error.value = msg;
      isConnected.value = false;
      tools.value = [];
      client = null;
      showError(msg);
    } finally {
      isLoading.value = false;
    }
  }

  function handleDisconnect() {
    client = null;
    isConnected.value = false;
    tools.value = [];
    selectedTool.value = null;
    error.value = null;
  }

  function handleSelectTool(toolId: string) {
    selectedTool.value = tools.value.find((t) => t.id === toolId) ?? null;
  }

  async function handleExecuteTool(parameters: Record<string, unknown>) {
    if (!selectedTool.value || !client) return;

    toolInspectorRef.value?.setExecuting(true);
    const start = Date.now();

    try {
      const result = await client.callTool(selectedTool.value.name, parameters);
      const duration = Date.now() - start;
      const isError = result.isError === true;
      const response: ToolResponse = {
        success: !isError,
        data: result,
        duration,
        ...(isError && {
          error: {
            code: 'TOOL_ERROR',
            message: 'Tool reported an error during execution',
            details: result,
          },
        }),
      };
      toolInspectorRef.value?.setResponse(response);
      if (isError) {
        showError(`Tool "${selectedTool.value.name}" returned an error`);
      } else {
        showSuccess(`Tool "${selectedTool.value.name}" executed`);
      }
    } catch (e) {
      const duration = Date.now() - start;
      const msg = toUserMessage(e, 'Unknown error');
      toolInspectorRef.value?.setResponse({
        success: false,
        duration,
        error: { code: 'RPC_ERROR', message: msg },
      });
      showError(msg);
    }
  }

  async function loadVServers() {
    const orgId = currentOrganizationId.value;
    if (!orgId) return;
    await vserversStore.fetchVServers(orgId);
    if (!selectedServerSlug.value && activeServers.value.length > 0) {
      selectedServerSlug.value = activeServers.value[0].slug;
    }
  }

  onMounted(async () => {
    await loadVServers();
  });

  watch(
    () => currentOrganizationId.value,
    async () => {
      handleDisconnect();
      selectedServerSlug.value = '';
      await loadVServers();
    },
  );

  watch(
    () => [props.authMethod, props.authToken],
    () => {
      if (isConnected.value) handleDisconnect();
    },
  );
</script>

<style scoped>
  .mcp-playground {
    @apply w-full h-full;
  }
</style>
